import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';

import React, { useCallback, useMemo, useState } from 'react';
import classnames from 'clsx';
import { TransactionCategory } from '@shipstation/financial-services-ui-components';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Text, TextType } from 'ui-components/components/text';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';

import { FormattedPriceBlock } from './formatted-price-block';
import priceBlockStyles from './formatted-price-block.module.less';
import { messages } from './messages';
import { messages as yourCarriersMessages } from '../your-carriers/messages';
import { PlanSummaryPaymentMethod } from './plan-summary-payment-method';
import styles from './plan-summary.module.less';
import { UpdatePlanButton } from './update-plan-button';
import { useManageSubscriptionDrawerSectionVisibility, PlanGridConfig } from '../use-manage-subscription-drawer-section-visibility';
import { FormattedCurrency } from '../../../../../../components/formatted-currency';
import FormattedCurrencyContainer from '../../../../../../components/formatted-currency/container';
import { UserDateFormat } from '../../../../../../components/user-date-format';
import { emptyObject } from 'ui-components/default-props/empty';
import { BillingPlanIds } from '../../../../../../enums/billing-plan-ids';
import { useEnableAddOnCentral } from '../../../../../../feature-flags/hooks/use-enable-add-on-central';
import { FinancialServicesUIProviderWrapper } from '../../../../../../financial-services-ui/financial-services-ui-provider-wrapper';
import FsuiContextProvider, { SOURCE_SUBSCRIPTION_CHANGE_PLAN } from '../../../../../../financial-services-ui/fsui-context-provider';
import { getBillingInfoFromPaymentMethod } from '../../../../../../financial-services-ui/mapping';
import { pollForNewPaymentMethod } from '../../../../../../financial-services-ui/poll-for-new-payment-method';
import {
  useIsNonWalletAccessVisible,
  useNonWalletAccessBillingState,
  useNonWalletAccessDescription,
  useIsByoaPriceUnknownDuringTrial,
  NonWalletAccessBillingState,
  getNonWalletAccessDisplayPrice,
  getNonWalletAccessDisplayPriceNote,
  getBasePlanForNonWalletAccessPlan,
} from '../../../../../../hooks/non-wallet-access-hooks';
import { SUBSCRIPTION_CHANGE_PLAN_DATA } from '../../../../../../hooks/use-local-storage';
import { useModal } from '../../../../../../hooks/use-modal';
import { usePaymentMethodManager } from '../../../../../../hooks/use-payment-method-manager';
import Global from '../../../../../../store/global-selectors';
import { UNLIMITED_SHIPMENT_LIMIT } from '../../../constants/shipment-limits';
import { usePaymentAndSubscriptionMegaContext } from '../../../context';
import { useManageSubscriptionDrawerContext } from '../../../context/manage-subscription-drawer-context';
import { useGetNumberOfExtraUserSeats } from '../../../hooks/use-get-number-of-extra-user-seats';
import { useGetUserSeatsAddOnData } from '../../../hooks/use-get-user-seats-addon-data';
import {
  getPlanShipmentLimitMessage,
  useSelectedBasePlan,
  useGetNumberOfIncludedUserSeatsForPlan,
  useGetNumberOfIncludedAnalyticsLicensesForPlan,
  useShipmentLimitCostForBillingPlan,
} from '../../../hooks/use-plan-details';
import { useAnalyticsLicenseData } from '../../../hooks/use-analytics-license-data';
import { AddPaymentMethodModal } from '../../manage-payment-methods/add-payment-method-modal';
import { useGetSimplifiedPlanExperimentName } from '../../plan-name-helper';
import { PriceValue } from '../../price-value';
import { getLineItemPlanSummaryForDisplay } from '../helpers';
import { useEffectivePlanDiscount } from '../../../hooks/use-coupon';
import { DEFAULT_USER_ADDON_PRICE, getDefaultAnalyticsLicenseAddonPrice } from '../constants';

const createToggleHandler = (handler, code, isDeactivated) => () => handler(code, isDeactivated);
/**
 * @typedef {import('@shipstation/financial-services-ui-components').BillingInfoData} BillingInfoData
 */

export const PlanSummary = () => {
  const useEnableAddOnMarketplace = useEnableAddOnCentral();
  const { planGridConfig, canMigrate } = useManageSubscriptionDrawerSectionVisibility();
  const intl = useIntl();
  const subscriptionCurrencyCode = useSelector(Global.selectSellerSubscriptionCurrency);
  const seller = useSelector((state) => state.seller) || emptyObject;

  // Plan info
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const byoaBillingState = useNonWalletAccessBillingState();
  const {
    byoaEnabled,
    desiredNumberOfAdditionalUsers,
    desiredNumberOfAdditionalAnalyticsLicenses,
    visibleLineItems,
    addOnsToAdd,
    addOnsToRemove,
    setAddOnsToAdd,
    setAddOnsToRemove,
    desiredShipmentLimit,
    preloadedSession,
    initialChangePayload,
    isOnAnnualBilling,
    isMigratingToNewestPlans,
    currentSubscriptionShipmentLimit,
    selectedPlanId,
    transitioningAddOnCodes,
  } = useManageSubscriptionDrawerContext();

  // Use new lineup style (separate Volume section) when viewing GbbLineup grid
  // Legacy grid always shows legacy style (shipments as subtext in plan section)
  const shouldUseNewLineupStyle = planGridConfig === PlanGridConfig.GbbLineup;

  // if selected plan is a BYOA plan, for _display purposes_ we want to show it as the base plan from allPlans
  const selectedPlan = useSelectedBasePlan();

  // Check if selected plan is in the active grid's plan list
  // If not (e.g., legacy plan while viewing migration grid), use current subscription data
  const activePlans = isMigratingToNewestPlans ? subscriptionData?.defaultProfilePlans : subscriptionData?.allPlans;
  const isSelectedPlanInActiveGrid = activePlans?.some((plan) => plan.billingPlanId?.toString() === selectedPlanId?.toString()) ?? false;
  const isTrialSeller = subscriptionData?.currentBillingPlan?.billingPlanId?.toString() === BillingPlanIds.Trial.toString();
  const shouldUseCurrentSubscriptionData = !isTrialSeller && !isSelectedPlanInActiveGrid;

  // When selected plan isn't in active grid, fall back to current subscription values
  const currentBillingPlan = subscriptionData?.currentBillingPlan;
  const numberOfExtraUserSeats = useGetNumberOfExtraUserSeats();
  const effectivePlan = shouldUseCurrentSubscriptionData ? (currentBillingPlan ?? selectedPlan) : selectedPlan;
  const effectiveShipmentLimit = shouldUseCurrentSubscriptionData ? currentSubscriptionShipmentLimit : (desiredShipmentLimit ?? 0);
  const effectiveAdditionalUsers = shouldUseCurrentSubscriptionData ? numberOfExtraUserSeats : (desiredNumberOfAdditionalUsers ?? 0);

  const isSelectedPlanAnnual = effectivePlan?.isOnAnnualBilling ?? isOnAnnualBilling;
  const numberOfShipments = getPlanShipmentLimitMessage(effectivePlan);

  const effectivePlanShipmentLimitCost = useShipmentLimitCostForBillingPlan(effectivePlan?.billingPlanId, effectiveShipmentLimit);
  const shipmentLimitCost = useMemo(() => {
    return shouldUseNewLineupStyle && effectivePlanShipmentLimitCost ? effectivePlanShipmentLimitCost : { value: 0 };
  }, [shouldUseNewLineupStyle, effectivePlanShipmentLimitCost]);

  // BYOA/Your Carriers
  const shouldShowByoaToggle = useIsNonWalletAccessVisible();
  const { planToNonWalletAccessPlanMap, allPlans } = subscriptionData ?? {};

  // Check if effectivePlan is a BYOA plan (exists as a value in the map)
  const isEffectivePlanByoa = Object.values(planToNonWalletAccessPlanMap ?? {}).some(
    (byoa) => byoa.billingPlanId === effectivePlan?.billingPlanId,
  );

  // If BYOA plan, get its base plan for price calculation
  const basePlanForPricing = isEffectivePlanByoa
    ? getBasePlanForNonWalletAccessPlan(effectivePlan, planToNonWalletAccessPlanMap, allPlans)
    : effectivePlan;

  const planPrice = basePlanForPricing?.pricePerPeriod?.value || 0;

  const byoaPlan = planToNonWalletAccessPlanMap?.[basePlanForPricing?.billingPlanId];
  const byoaStateLabel = byoaEnabled ? messages.active : messages.inactive;
  const isByoaFree = byoaBillingState === NonWalletAccessBillingState.Included;
  const isByoaPriceUnknown = useIsByoaPriceUnknownDuringTrial(basePlanForPricing);
  const byoaAddOnPrice = byoaEnabled && !isByoaFree ? getNonWalletAccessDisplayPrice(basePlanForPricing, byoaPlan) : 0;
  const byoaPriceNote = isByoaFree ? '' : getNonWalletAccessDisplayPriceNote(basePlanForPricing);
  const byoaDescription = useNonWalletAccessDescription({ selectedPlan: basePlanForPricing });

  // Analytics Licenses
  const analyticsLicenseData = useAnalyticsLicenseData();
  const numberOfAnalyticsLicensesIncludedForEffectivePlan = useGetNumberOfIncludedAnalyticsLicensesForPlan(effectivePlan?.billingPlanId);
  const effectiveAdditionalAnalyticsLicenses = shouldUseCurrentSubscriptionData
    ? (analyticsLicenseData.additionalLicenses ?? 0)
    : (desiredNumberOfAdditionalAnalyticsLicenses ?? 0);
  const totalAnalyticsLicensesForEffectivePlan = numberOfAnalyticsLicensesIncludedForEffectivePlan + effectiveAdditionalAnalyticsLicenses;
  const hasAnalyticsLicenses = numberOfAnalyticsLicensesIncludedForEffectivePlan > 0 || effectiveAdditionalAnalyticsLicenses > 0;

  const effectiveAnalyticsAddOnPrice = useMemo(() => {
    const defaultPrice = getDefaultAnalyticsLicenseAddonPrice(effectivePlan?.billingPlanId);
    return {
      value: effectivePlan?.analyticsLicenseAddOnPrice?.value ?? analyticsLicenseData.unitCost.value ?? defaultPrice,
      code: effectivePlan?.analyticsLicenseAddOnPrice?.code ?? analyticsLicenseData.unitCost.code ?? subscriptionCurrencyCode,
    };
  }, [effectivePlan?.analyticsLicenseAddOnPrice, effectivePlan?.billingPlanId, analyticsLicenseData.unitCost, subscriptionCurrencyCode]);

  const totalAnalyticsLicensePrice = effectiveAdditionalAnalyticsLicenses * effectiveAnalyticsAddOnPrice.value;

  // Users
  const numberOfUserSeatsIncludedForEffectivePlan = useGetNumberOfIncludedUserSeatsForPlan(effectivePlan?.billingPlanId);
  const totalUserSeatsForEffectivePlan = numberOfUserSeatsIncludedForEffectivePlan + effectiveAdditionalUsers;
  const userAddOnData = useGetUserSeatsAddOnData();
  const { unitCost: userAddOnUnitCost } = userAddOnData;
  // Use selected plan's user add-on price (important for annual plans which have different pricing)
  // Fallback chain: plan's userAddOnPrice -> hook's unitCost -> default price (for legacy plans)
  const effectiveUserAddOnPrice = useMemo(
    () => ({
      value: effectivePlan?.userAddOnPrice?.value ?? userAddOnUnitCost.value ?? DEFAULT_USER_ADDON_PRICE,
      code: effectivePlan?.userAddOnPrice?.code ?? userAddOnUnitCost.code ?? subscriptionCurrencyCode,
    }),
    [effectivePlan?.userAddOnPrice, userAddOnUnitCost, subscriptionCurrencyCode],
  );

  // Features & AddOns
  const handleToggleAddOn = useCallback(
    (addOnCode, isCurrentlyDeactivated) => {
      if (isCurrentlyDeactivated) {
        setAddOnsToAdd((prev) => [...prev, addOnCode]);
        setAddOnsToRemove((prev) => prev.filter((a) => a !== addOnCode));
      } else {
        setAddOnsToAdd((prev) => prev.filter((a) => a !== addOnCode));
        setAddOnsToRemove((prev) => [...prev, addOnCode]);
      }
    },
    [setAddOnsToAdd, setAddOnsToRemove],
  );

  const { lineItems: planSummaryLineItems, lineItemsForDisplay: planSummaryLineItemsForDisplay } = useMemo(() => {
    const lineItems = getLineItemPlanSummaryForDisplay(
      visibleLineItems,
      addOnsToAdd,
      addOnsToRemove,
      isMigratingToNewestPlans,
      transitioningAddOnCodes,
    );
    const lineItemsForDisplay = lineItems.map((lineItem) => {
      const toggleMessage = lineItem.isDeactivated ? messages.addAddOn : messages.removeAddOn;

      const getDescription = () => {
        if (lineItem.isTrial) {
          return (
            <div className={styles['trial-line-item-descriptor']}>
              <Text
                formattedMessageValues={{
                  addonName: lineItem.nameMessageDescriptor.defaultMessage,
                }}
                textType={TextType.Caption}
                value={messages.addOnNameInTrial}
              />
              <Tooltip position='bottom'>
                <div className={styles['trial-info-icon-container']}>
                  <FontAwesomeIcon icon={faInfoCircle} />
                </div>
                <TooltipContent>
                  {lineItem.postTrialPrice?.value ? (
                    <FormattedMessage
                      {...messages.addOnTrialDisclaimer}
                      values={{
                        paymentDate: <UserDateFormat value={lineItem.trialEndDateTimeUtc} />,
                        price: (
                          <FormattedCurrencyContainer
                            currencyCode={lineItem.postTrialPrice.code ?? subscriptionCurrencyCode}
                            fractionDigits={2}
                            value={lineItem.postTrialPrice.value}
                          />
                        ),
                      }}
                    />
                  ) : (
                    <FormattedMessage
                      {...messages.addOnTrialDisabledAfterTrial}
                      values={{
                        paymentDate: <UserDateFormat value={lineItem.trialEndDateTimeUtc} />,
                      }}
                    />
                  )}
                </TooltipContent>
              </Tooltip>
            </div>
          );
        }

        const name = intl.formatMessage(lineItem.nameMessageDescriptor);
        if (lineItem.isTransitioning) {
          return (
            <span>
              {name} <FontAwesomeIcon className={priceBlockStyles['warning-icon']} icon={faExclamationTriangle} />
            </span>
          );
        }

        return name;
      };

      return {
        key: lineItem.code,
        ariaLabel: intl.formatMessage(messages.addonsPriceLabel, {
          addonName: lineItem.nameMessageDescriptor.defaultMessage,
        }),
        description: getDescription(),
        lineItemPrice: lineItem.displayPrice,
        actionElement: lineItem.isToggleable ? (
          <Button
            ariaLabel={intl.formatMessage(toggleMessage)}
            className={priceBlockStyles['toggle-addon-btn']}
            onClick={createToggleHandler(handleToggleAddOn, lineItem.addOnCode, lineItem.isDeactivated)}
            theme={THEME_CONSTANTS.link}
          >
            <FormattedMessage {...toggleMessage} />
          </Button>
        ) : null,
      };
    });

    return {
      lineItems,
      lineItemsForDisplay,
    };
  }, [
    addOnsToRemove,
    intl,
    visibleLineItems,
    addOnsToAdd,
    isMigratingToNewestPlans,
    handleToggleAddOn,
    transitioningAddOnCodes,
    subscriptionCurrencyCode,
  ]);

  // Pricing. (NOTE): This presumes the same currencies
  const featureCosts = planSummaryLineItems.reduce((sum, current) => {
    return current.isDeactivated ? sum : sum + current.price.value;
  }, 0);
  const totalUsersPrice = effectiveAdditionalUsers * effectiveUserAddOnPrice.value;

  // Calculate discount (applies to plan + volume + users + analytics subtotal)
  const discountableSubtotal = planPrice + totalUsersPrice + totalAnalyticsLicensePrice + shipmentLimitCost.value;
  const { effectiveDiscount, isCouponDiscount } = useEffectivePlanDiscount(
    effectivePlan?.code,
    discountableSubtotal,
    effectiveShipmentLimit,
  );
  const discountAmount = effectiveDiscount?.hasDiscount ? effectiveDiscount.savingsAmount : 0;

  const totalPrice =
    planPrice + byoaAddOnPrice + totalUsersPrice + totalAnalyticsLicensePrice + featureCosts + shipmentLimitCost.value - discountAmount;

  const hasTransitioningAddOns = planSummaryLineItems.some((item) => item.isTransitioning);

  const planName = useGetSimplifiedPlanExperimentName(effectivePlan, {
    shouldShowShipmentLimit: false,
  });

  const { isOpen, open, close } = useModal();
  const { reloadData, paymentMethodDetails: paymentMethods } = usePaymentAndSubscriptionMegaContext();
  const [isPolling, setIsPolling] = useState(false);

  const handleAddPaymentMethod = useCallback(() => {
    setIsPolling(true);
    pollForNewPaymentMethod({
      reloadData,
      previousCount: paymentMethods?.length ?? 0,
    }).then(() => {
      setIsPolling(false);
      close();
    });
  }, [close, reloadData, paymentMethods]);

  const { preferredPaymentMethod } = usePaymentMethodManager({
    transactionCategory: TransactionCategory.Subscription,
    initialPaymentMethod: initialChangePayload.newPaymentMethod,
  });

  const initialBillingInfo = useMemo(
    () => getBillingInfoFromPaymentMethod(preferredPaymentMethod, seller),
    [preferredPaymentMethod, seller],
  );

  const additionalStorePayload = useMemo(() => {
    const obj = {
      selectedPlanId: selectedPlan?.billingPlanId,
      desiredNumberOfAdditionalUsers,
      desiredShipmentLimit,
      addOnsToAdd,
      addOnsToRemove,
      existingPaymentMethodIds: paymentMethods?.map((pm) => pm.id) || [],
    };
    return obj;
  }, [addOnsToAdd, addOnsToRemove, desiredNumberOfAdditionalUsers, desiredShipmentLimit, paymentMethods, selectedPlan?.billingPlanId]);

  return (
    <div className={`${styles['plan-summary-wrapper']} ${canMigrate ? styles['with-tab-offset'] : ''}`}>
      <Text className={styles['plan-summary-title']} element='h4' textType={TextType.H3} value={messages.planSummaryTitle} />
      {shouldUseNewLineupStyle ? (
        <>
          <FormattedPriceBlock
            blockSectionTitle={<FormattedMessage {...messages.planTitle} />}
            className={styles['compact-line-item']}
            price={<PriceValue plan={basePlanForPricing} />}
            summaryDetailList={[
              {
                key: 'planName',
                description: planName,
              },
            ]}
          />
          <FormattedPriceBlock
            blockSectionTitle={<FormattedMessage {...messages.volumeTitle} />}
            className={styles['compact-line-item']}
            price={<FormattedCurrency currencyCode={shipmentLimitCost.code} value={shipmentLimitCost.value} />}
            summaryDetailList={[
              {
                key: 'shipmentLimit',
                description: (
                  <FormattedMessage
                    {...messages.shipmentsPerMonth}
                    values={{
                      value:
                        effectiveShipmentLimit === UNLIMITED_SHIPMENT_LIMIT ? (
                          <FormattedMessage {...messages.unlimited} />
                        ) : (
                          <FormattedNumber value={effectiveShipmentLimit} />
                        ),
                    }}
                  />
                ),
              },
            ]}
          />
        </>
      ) : (
        <FormattedPriceBlock
          blockSectionTitle={planName}
          price={<PriceValue plan={basePlanForPricing} />}
          summaryDetailList={[
            {
              key: 'shipmentLimit',
              description: <FormattedMessage {...messages.shipmentsPerMonth} values={{ value: numberOfShipments }} />,
            },
          ]}
        />
      )}
      <FormattedPriceBlock
        blockSectionTitle={intl.formatMessage(messages.users, {
          value: totalUserSeatsForEffectivePlan,
        })}
        className={shouldUseNewLineupStyle ? styles['compact-line-item'] : undefined}
        summaryDetailList={[
          {
            key: 'includedUsers',
            description: <FormattedMessage {...messages.includedUsers} values={{ value: numberOfUserSeatsIncludedForEffectivePlan }} />,
          },
          {
            key: 'additionalUsers',
            ariaLabel: intl.formatMessage(messages.additionalUsersTotalPrice),
            description: <FormattedMessage {...messages.additionalUsers} values={{ value: effectiveAdditionalUsers }} />,
            lineItemPrice: <FormattedCurrency currencyCode={effectiveUserAddOnPrice.code} value={totalUsersPrice} />,
          },
        ]}
      />
      {hasAnalyticsLicenses ? (
        <FormattedPriceBlock
          blockSectionTitle={intl.formatMessage(messages.analyticsLicenses, {
            value: totalAnalyticsLicensesForEffectivePlan,
          })}
          className={shouldUseNewLineupStyle ? styles['compact-line-item'] : undefined}
          summaryDetailList={[
            {
              key: 'includedAnalyticsLicenses',
              description: (
                <FormattedMessage
                  {...messages.includedAnalyticsLicenses}
                  values={{ value: numberOfAnalyticsLicensesIncludedForEffectivePlan }}
                />
              ),
            },
            {
              key: 'additionalAnalyticsLicenses',
              ariaLabel: intl.formatMessage(messages.additionalAnalyticsLicensesTotalPrice),
              description: (
                <FormattedMessage {...messages.additionalAnalyticsLicenses} values={{ value: effectiveAdditionalAnalyticsLicenses }} />
              ),
              lineItemPrice: <FormattedCurrency currencyCode={effectiveAnalyticsAddOnPrice.code} value={totalAnalyticsLicensePrice} />,
            },
          ]}
        />
      ) : null}
      {shouldShowByoaToggle && !shouldUseNewLineupStyle ? (
        <FormattedPriceBlock
          blockSectionTitle={
            <FormattedMessage
              {...messages.yourCarriersTitle}
              values={{
                value: <FormattedMessage {...byoaStateLabel} />,
              }}
            />
          }
          price={
            isByoaPriceUnknown ? (
              <FormattedMessage {...yourCarriersMessages.yourCarriersFreeLabel} />
            ) : (
              <>
                <FormattedCurrencyContainer currencyCode={byoaPlan?.pricePerPeriod?.code} fractionDigits={2} value={byoaAddOnPrice} />
                {byoaPriceNote}
              </>
            )
          }
          summaryDetailList={[
            {
              key: 'byoa',
              description: byoaDescription,
            },
          ]}
        />
      ) : null}
      {useEnableAddOnMarketplace && planSummaryLineItemsForDisplay.length > 0 ? (
        <FormattedPriceBlock
          blockSectionTitle={<FormattedMessage {...messages.addonsTitle} />}
          className={shouldUseNewLineupStyle ? styles['compact-line-item'] : undefined}
          summaryDetailList={planSummaryLineItemsForDisplay}
        />
      ) : null}
      {useEnableAddOnMarketplace && planSummaryLineItemsForDisplay.length === 0 && !shouldUseNewLineupStyle ? (
        <FormattedPriceBlock
          blockSectionTitle={<FormattedMessage {...messages.addonsTitle} />}
          className={shouldUseNewLineupStyle ? styles['compact-line-item'] : undefined}
          summaryDetailList={[
            {
              description: <FormattedMessage {...messages.none} />,
              key: 'noAddOns',
            },
          ]}
        />
      ) : null}
      {effectiveDiscount?.hasDiscount ? (
        <FormattedPriceBlock
          blockSectionTitle={<FormattedMessage {...messages.discountTitle} />}
          className={shouldUseNewLineupStyle ? styles['compact-line-item'] : undefined}
          price={
            <span className={isCouponDiscount ? styles['coupon-discount-price'] : styles['discount-price']}>
              -<FormattedCurrency currencyCode={effectiveUserAddOnPrice.code} value={discountAmount} />
            </span>
          }
          summaryDetailList={[
            {
              key: 'subscriptionSavings',
              description: (
                <FormattedMessage
                  {...messages.subscriptionSavings}
                  values={{
                    percent: <FormattedNumber maximumFractionDigits={0} style='percent' value={effectiveDiscount.discountPercent / 100} />,
                  }}
                />
              ),
            },
          ]}
        />
      ) : null}
      <FlexRow
        className={classnames(styles['total-price-section'], {
          [styles['with-border-top']]: shouldUseNewLineupStyle,
        })}
      >
        <FlexColumn>
          <FormattedMessage {...(isSelectedPlanAnnual ? messages.yearlyTotal : messages.monthlyTotal)} />
        </FlexColumn>
        <FlexColumn ariaLabel={intl.formatMessage(isSelectedPlanAnnual ? messages.yearlyTotal : messages.monthlyTotal)}>
          <FormattedCurrency currencyCode={effectiveUserAddOnPrice.code} value={totalPrice} />
        </FlexColumn>
      </FlexRow>
      {hasTransitioningAddOns ? (
        <BannerMessageV2
          className={styles['downgrade-warning-banner']}
          content={<FormattedMessage {...messages.downgradeAddOnWarning} />}
          status={BannerStatus.Warning}
        />
      ) : null}
      <FsuiContextProvider
        currencyCode={subscriptionCurrencyCode}
        shouldSkipSessionCreation
        transactionCategory={TransactionCategory.Subscription}
      >
        <FinancialServicesUIProviderWrapper initialBillingInfo={initialBillingInfo}>
          <PlanSummaryPaymentMethod initialPaymentMethod={initialChangePayload.newPaymentMethod} onAddAction={open} />
        </FinancialServicesUIProviderWrapper>
        <UpdatePlanButton />
        <Text className={styles['tax-note']} textType={TextType.Caption} value={messages.taxNote} />
      </FsuiContextProvider>
      {isOpen ? (
        <FsuiContextProvider
          addtlReturnParams={SOURCE_SUBSCRIPTION_CHANGE_PLAN}
          currencyCode={subscriptionCurrencyCode}
          preloadedSession={preloadedSession}
          transactionCategory={null}
        >
          <FinancialServicesUIProviderWrapper>
            <AddPaymentMethodModal
              additionalStorePayload={additionalStorePayload}
              dataSource={SUBSCRIPTION_CHANGE_PLAN_DATA}
              isPolling={isPolling}
              onCancel={close}
              onConfirm={handleAddPaymentMethod}
            />
          </FinancialServicesUIProviderWrapper>
        </FsuiContextProvider>
      ) : null}
    </div>
  );
};
